import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { ERROR_MESSAGES } from 'Utils/constants';
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import DocumentTemplate from 'Components/Public/DocumentTemplate';
import Feedback from 'Components/Public/Feedback/Feedback';
import { ArticleHeader, RelatedArticles } from 'Components/Public/ArticleTemplate';
import { loadArticleData } from 'Content/ArticleData/ArticleRegistry';
import { findArticleCall, filterRelatedArticlesCall } from './ArticleTemplateAxiosFuncs';
import { PATHS } from 'Utils/PagePaths';

const ArticleTemplate = ({ setCustomCrumb }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const sectionRefs = useRef({});
  const suggestedRef = useRef(null);

  const [crumb, setCrumb] = useState(null);
  const [suggestedArticles, setSuggestedArticles] = useState([]);

  useEffect(() => {
    if (crumb != null) setCustomCrumb(crumb);
  }, [crumb]);

  useEffect(() => {
    setIsLoading(true);
    setArticle(null);
    setSuggestedArticles([]);

    Promise.all([findArticleCall(Number(id)), loadArticleData(id)])
      .then(([headerRes, sectionsData]) => {
        const notFound = headerRes?.status !== 1
          || headerRes?.globalErrorMessageList?.length > 0
          || !headerRes?.data?.title
          || !sectionsData;

        if (notFound) {
          setArticle(null);
        } else {
          setArticle({
            type: headerRes.data.categoryShortName,
            topics: headerRes.data.topic,
            title: headerRes.data.title,
            issueNumber: headerRes.data.issueNumber,
            publishDate: headerRes.data.publishMonthYear,
            lastEditedDate: headerRes.data.editMonthYear,
            primaryTopicId: headerRes.data.primaryTopicId,
            downloadGroupId: sectionsData.downloadGroupId,
            sections: sectionsData.sections,
          });
        }
      })
      .catch(() => {
        setArticle(null);
        setGlobalErrorStatus({
          dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: { redirect: false, redirectFirst: false, path: "", data: {} }
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (isLoading) return;
    if (article == null) {
      setCrumb({ replace: true, value: 'Article Not Found' });
    } else {
      setCrumb({ replace: true, value: article?.title });
    }
  }, [article, isLoading]);

  useEffect(() => {
    if (!article?.primaryTopicId) return;
    setSuggestedArticles([]);
    const currentGroupId = Number(id);
    filterRelatedArticlesCall(article.primaryTopicId)
      .then((res) => {
        if (res?.status !== 1 || res?.globalErrorMessageList?.length > 0) {
          throw new Error(res?.globalErrorMessageList?.[0] || ERROR_MESSAGES.DATA_ERROR.message);
        }
        const rawArticles = Array.isArray(res?.data) ? res.data : [];
        const seenDocIds = new Set();
        const dedupedArticles = rawArticles.filter((a) => {
          if (a.groupId === currentGroupId) return false;
          if (seenDocIds.has(a.docId)) return false;
          seenDocIds.add(a.docId);
          return true;
        }).slice(0, 6);
        setSuggestedArticles(dedupedArticles);
      })
      .catch(() => {
        setSuggestedArticles([]);
        setGlobalErrorStatus({
          dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: { redirect: false, redirectFirst: false, path: "", data: {} }
        });
      });
  }, [article?.primaryTopicId]);

  if (isLoading) {
    return null;
  }

  if (!article) {
    return (
      <Container className="text-center py-5">
        <h1 className="header2">Article Not Found</h1>
        <p className="body mt-3">
          The article you are looking for does not exist or may have been removed.
        </p>
        <Link to={PATHS.public['PSOPPC Articles']} className="btn btn-normal mt-3">
          Browse All Articles
        </Link>
      </Container>
    );
  }

  const shareTitle = article.issueNumber
    ? `${article.title} (Issue ${article.issueNumber})`
    : article.title;

  const hasRelated = suggestedArticles.length > 0;

  return (
    <>
      <Helmet>
        <title>{article.title} | PSOPPC</title>
        <meta property="og:title" content={shareTitle} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={shareTitle} />
      </Helmet>
      <ArticleHeader article={article} />
      <DocumentTemplate
        commonFormatsData={article.sections}
        ref={sectionRefs}
        extraSections={hasRelated ? [{ title: 'Suggested Articles', ref: suggestedRef }] : []}
      />
      {hasRelated && (
        <div ref={suggestedRef} className='p-0'>
          <RelatedArticles articles={suggestedArticles} topics={article.topics} />
        </div>
      )}
      <Feedback />
    </>
  );
};

export default ArticleTemplate;
